import {
  CIFDoc,
  CIFsCollection,
  Database,
  Firestore,
  GroupCheckExistence,
  GroupDoc,
  GroupsCollection,
  OperatorDoc,
  GetDocFromReference,
} from '@/firebase-exports'
import { getCIF } from '@/services/cifs-service'
import { getOperator } from '@/services/operator-service'
import store from '@/store/index'

/**
 * Returns a list of the groups.
 * @returns Gets a list of the groups.
 */
export async function listGroups(id = undefined) {
  const userId = id
    ? id
    : store.state.user.parentRef
    ? store.state.user.parentRef.id
    : store.state.user.id

  const querySnapshot = await Firestore.getDocs(GroupsCollection(userId))
  return (
    querySnapshot.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      // Ordenar los grupos para ser mostrados alfabeticamente
      .sort((a, b) =>
        a.name.localeCompare(b.name, 'es', {
          sensitivity: 'base',
        })
      )
  )
}

/**
 * Gets the unregistered cifs of a company.
 * @param {Array} group Group that contains the unregistered cifs.
 * @returns Array with the unregistered cifs.
 */
export async function getUnregisteredCifs(group) {
  const unregisteredCifs = group.unregisteredCifs ?? []

  return await Promise.all(
    unregisteredCifs.map(async (ref) => ({
      id: ref.id,
      ...(await Firestore.getDoc(ref)).data(),
    }))
  )
}

/**
 * Returns a list of the user groups.
 * @returns User groups.
 */
export async function listUserGroups() {
  return await Promise.all(
    store.state.user.groupsRef?.map(async (ref) => {
      const doc = await Firestore.getDoc(ref)
      return { id: doc.id, ...doc.data() }
    }) ?? []
  )
}

/**
 * Returns an object with the group
 * @param {Object} ref Reference of the group.
 * @returns Object with the group.
 */
export async function getGroup(ref) {
  const doc = await Firestore.getDoc(ref)
  if (!doc.exists) return null

  return { id: doc.id, ...doc.data() }
}

/**
 * Given a group it returns the unregistered cifs of this group.
 * @param {Object} unregisteredCifs List of unregisteredCifs.
 * @returns List with the unregistered cifs of the group.
 */
export async function getGroupUnregisteredCifs(unregisteredCifs = []) {
  return await Promise.all(
    unregisteredCifs?.map(async (ref) => ({
      id: ref.id,
      ...(await Firestore.getDoc(ref)).data(),
    }))
  )
}

/**
 * Returns an object with the groups and an array with the groups
 * @returns Object with groups.
 */
export async function getGroups() {
  const groupsMap = {}

  const groups = await listGroups()
  groups.forEach((group) => (groupsMap[group.id] = group))

  return { groupsMap, groups }
}

/**
 * Creates a subscription to the groups of a user.
 * @param {Object} context Context of the view.
 * @param {Boolean?} loadUnregistered Flag to load the unregistered clients.
 * @returns Unsubscribe method to the subscription.
 */
export function getGroupsSubscription(context, loadUnregistered = false) {
  const userId = store.state.user.parentRef
    ? store.state.user.parentRef.id
    : store.state.user.id

  var groupUnsubscribe = Firestore.onSnapshot(
    Firestore.query(GroupsCollection(userId), Firestore.orderBy('name')),
    (snapShot) => {
      const groups = snapShot.docs.map((doc) => {
        const data = doc.data()

        const fullName = typeof data.name === 'string' ? data.name : ''
        delete data.name

        return {
          id: doc.id,
          ...data,
          fullName,
        }
      })
      context.groups = groups

      if (loadUnregistered)
        context.groups.forEach(
          async (group) =>
            (group.unregisteredCifs = await getGroupUnregisteredCifs(
              group.unregisteredCifs
            ))
        )
    },
    (error) => {
      throw error
    }
  )
  return groupUnsubscribe
}

/**
 * Creates a group for the logged user.
 * @param {Object} data Data of the group.
 * @param {Array} operatorsId Operators that need to be added into the group.
 * @param {Array} cifsId Cifs that need to be added into the group.
 */
export const createGroup = async (data, operatorsId = []) => {
  const userId = store.state.user.parentRef
    ? store.state.user.parentRef.id
    : store.state.user.id

  const ref = await Firestore.addDoc(GroupsCollection(userId), data)

  const batch = Firestore.writeBatch(Database)

  operatorsId.forEach((id) =>
    batch.update(OperatorDoc(id), {
      groupsRef: Firestore.arrayUnion(ref),
    })
  )
  await batch.commit()
}

/**
 * Updates a group.
 * @param {string} groupId Id of the group to update.
 * @param {Object} data Data of the group.
 */
export const updateGroup = async (
  groupId,
  data,
  previousGroupOperators = [],
  operatorsId = [],
  previousGroupCifs = [],
  cifsId = []
) => {
  const userId = store.state.user.parentRef
    ? store.state.user.parentRef.id
    : store.state.user.id

  // Looking for the operators to add. Looking for the operators that are not in previousGroupOperators
  const operatorsToAdd = operatorsId.filter(
    (id) => !previousGroupOperators.map((operator) => operator.id).includes(id)
  )

  // Looking for the operators to delete. Looking for the operators that are not in operatorsId
  const operatorsToRemove = previousGroupOperators
    .map((operator) => operator.id)
    .filter((id) => !operatorsId.includes(id))

  const batch = Firestore.writeBatch(Database)

  // Updating the operators
  operatorsToAdd.forEach((id) =>
    batch.update(OperatorDoc(id), {
      groupsRef: Firestore.arrayUnion(GroupDoc(userId, groupId)),
    })
  )
  operatorsToRemove.forEach((id) =>
    batch.update(OperatorDoc(id), {
      groupsRef: Firestore.arrayRemove(GroupDoc(userId, groupId)),
    })
  )

  // --------------------------------
  // 2. Empresas y Operadores:
  // Actualiza todos los operadores del Grupo, añadiendo o borrando a cada operador los cifs de
  // empresas que se han añadido o borrado en el grupo.
  // --------------------------------

  // 2.1 Cifs empresas a añadir, que no estaban previamente en previousGroupCifs y ahora estan en cifsId
  const cifsToAdd = cifsId.filter(
    (id) => !previousGroupCifs.map((cif) => cif.id).includes(id)
  )

  if (cifsToAdd?.length) {
    // resolver primero, y una sola vez, el array de id's de los cifs a añadir
    const cifsById = new Map()
    await Promise.all(
      cifsToAdd.map(async (cifId) => {
        const cif = await getCIF(userId, cifId)
        cifsById.set(cifId, cif)
      })
    )

    // Añade la referencia a la empresa en los operadores del grupo,
    // pero SOLO a aquellos que tengan activo el checkbox addNewCifs y que no esten disabled.
    const operators = operatorsId

    // Resolver primero, y una sola vez, el array de id's de los operadores seleccionados
    const operatorsById = new Map()
    await Promise.all(
      operators.map(async (operatorId) => {
        const operator = await getOperator(operatorId)
        if (operator) operatorsById.set(operatorId, operator)
      })
    )

    for (const operatorId of operators) {
      const operator = operatorsById.get(operatorId)
      if (!operator) continue
      if (operator.addNewCifs && !operator.disabled) {
        for (const cifId of cifsToAdd) {
          const cif = cifsById.get(cifId)
          if (!cif) continue

          batch.update(OperatorDoc(operator.id), {
            cifsRef: Firestore.arrayUnion(CIFDoc(userId, cif.id)),
            [`cifsPermissions.${cif.numeroDocIdentidad}`]: {
              firmasActivas: Boolean(
                cif.firmasActivas && operator.firmasActivas
              ),
              notificacionesActivas: Boolean(
                cif.notificacionesActivas && operator.notificacionesActivas
              ),
              representacionActivas: false,
            },
          })
        }
      }
    }
  }

  // 2.2 Cifs empresas a borrar, que estaban previamente en previousGroupCifs y que no estan ahora en cifsId
  const cifsToRemove = previousGroupCifs
    .map((cif) => cif.id)
    .filter((id) => !cifsId.includes(id))

  if (cifsToRemove?.length) {
    // Resolver primero, y una sola vez, el array de id's de los cifs a borrar
    const cifsById = new Map()
    await Promise.all(
      cifsToRemove.map(async (cifId) => {
        const cif = await getCIF(userId, cifId)
        cifsById.set(cifId, cif)
      })
    )

    // Borra la referencia a la empresa en los operadores del grupo
    const operators = previousGroupOperators
    for (const operator of operators) {
      for (const cifId of cifsToRemove) {
        const cif = cifsById.get(cifId)
        if (!cif) continue

        batch.update(OperatorDoc(operator.id), {
          cifsRef: Firestore.arrayRemove(CIFDoc(userId, cif.id)),
          [`cifsPermissions.${cif.numeroDocIdentidad}`]:
            Firestore.deleteField(),
        })
      }
    }
  }

  batch.update(GroupDoc(userId, groupId), data)
  await batch.commit()
}

/**
 * Deletes an group and removes the reference from the operators.
 * @param {String} groupId Id of the group to delete.
 * @param {Array} operators Operators that are in the group.
 */
export const deleteGroup = async (groupId, operators = []) => {
  const userId = store.state.user.parentRef
    ? store.state.user.parentRef.id
    : store.state.user.id

  const batch = Firestore.writeBatch(Database)

  // Deleting the reference in the operators
  operators.forEach(({ id }) =>
    batch.update(OperatorDoc(id), {
      groupsRef: Firestore.arrayRemove(GroupDoc(userId, groupId)),
    })
  )

  // Deleting the group
  //batch.delete(GroupDoc(userId, groupId))
  // Ahora, en lugar de borrar el grupo en la bbdd lo vamos a 'marcar' como borrado, y
  // vamos a conservar las refs a los cifs (cifsRefs), a los filtros (filtersRef) y a los usuarios (usersRef).
  batch.update(GroupDoc(userId, groupId), { deleted: true })

  await batch.commit()
}

/**
 * Adds a cif to a group
 * @param {String} cifId Id of the cif to add.
 * @param {String} groupId Id of the group where the cif
 * is going to be added.
 */
export const addCifToGroup = async (cifId, groupId) => {
  const userId = store.state.user.parentRef
    ? store.state.user.parentRef.id
    : store.state.user.id

  // Updating the group
  await Firestore.updateDoc(GroupDoc(userId, groupId), {
    cifsRef: Firestore.arrayUnion(CIFDoc(userId, cifId)),
  })
}

/**
 * Remove a cif from a group
 * @param {String} cifId Id of the cif to add.
 * @param {String} groupId Id of the group where the cif
 * is going to be added.
 */
export const removeCifFromGroup = async (cifId, groupId) => {
  const userId = store.state.user.parentRef
    ? store.state.user.parentRef.id
    : store.state.user.id

  // Updating the group
  await Firestore.updateDoc(GroupDoc(userId, groupId), {
    cifsRef: Firestore.arrayRemove(CIFDoc(userId, cifId)),
  })
}

/**
 * Adds a cif to a group
 * @param {String} cifId Id of the cif to add.
 * @param {Array} groupsId Array of ids of the groups where the cif must be included.
 * is going to be added.
 */
export const addCifToGroups = async (cifId, groupsId) => {
  for (const groupId of groupsId) await addCifToGroup(cifId, groupId)
}

/**
 * Checks if a cif exists in another group
 * @param {String} numeroDocIdentidadGoverment id of the company.
 * @returns False if the cif does not exists or the id of the existing cif.
 */
export const checkCifExistence = async (numeroDocIdentidad) => {
  const userId = store.state.user.parentRef
    ? store.state.user.parentRef.id
    : store.state.user.id

  const docs = await Firestore.getDocs(
    Firestore.query(
      CIFsCollection(userId),
      Firestore.where('numeroDocIdentidad', '==', numeroDocIdentidad)
    )
  )

  return docs.empty ? false : docs.docs[0].id
}

/**
 * Checks if a cif exists in another group
 * @param {String} toCheck Type of object to check:
 * - cif
 * - operator
 * @param {String} email Email of the operator to be created.
 * @returns False if the operator does not exists or the id of the operator.
 */
export const checkOperatorExistence = async (toCheck, email) => {
  const userId = store.state.user.parentRef
    ? store.state.user.parentRef.id
    : store.state.user.id

  const { data } = await GroupCheckExistence({
    toCheck,
    email,
    userId,
  })

  return data
}

/**
 * Function that removes a cif from the unregistered cifs list.
 * @param {String} cifId Id of the cif to remove.
 * @param {String} groupId Id of the group that has the unregistered cif.
 */
export const removeUnregistered = async (cifId, groupId) => {
  const userId = store.state.user.parentRef
    ? store.state.user.parentRef.id
    : store.state.user.id

  await Firestore.updateDoc(GroupDoc(userId, groupId), {
    unregisteredCifs: Firestore.arrayRemove(CIFDoc(userId, cifId)),
  })
}

/**
 * Function that returns a map with all the groups of each company.
 * @param {Array} groups List of groups.
 * @param {Array} cifs List of cifs.
 * @returns Object where the key is the id of cif and the value
 * are the groups where the cif is added.
 */
export const getCifGroupMap = (groups, cifs) => {
  const map = {}

  cifs.forEach((cif) => {
    const cifGroups = []
    groups.forEach((group) => {
      if (group.cifsRef.some((ref) => ref.id === cif.id)) cifGroups.push(group)
    })

    map[cif.id] = cifGroups
  })

  return map
}

/**
 * Dada una lista de ref a usuarios de un grupo, devuelve una lista de objetos con los datos de los usuarios (operadores) de dicho grupo.
 * @param {Object} usersRef Lista de referencias a usuarios.
 * @returns Lista con los datos de los usuarios de dicho grupo.
 */
export async function getGroupOperators(usersRef = []) {
  if (!usersRef.length) return []

  /*return await Promise.all(
    usersRef?.map(async (ref) => ({
      id: ref.id,
      ...(await Firestore.getDoc(ref)).data(),
    }))
  )*/

  // controlar la existencia del operador dado por su referencia y
  // devolver un array con los operadores que realmente existan.
  return Promise.all(
    usersRef.map(async (ref) => {
      const doc = await Firestore.getDoc(ref)
      if (!doc.exists) return null

      return {
        id: doc.id,
        ...doc.data(),
      }
    })
  ).then((users) => users.filter(Boolean))
}

/**
 * Dado un usuario, comprueba si pertenece al grupo a comprobar.
 * @param {Object} userRef referencia al usuario.
 * @param {Object} groupRef referencia al grupo.
 * @returns false si no pertenece.
 */
export async function userBelongsToGroup(userRef, groupRef) {
  const group = await getGroup(groupRef)
  if (!group) return false

  return group?.usersRef?.some((ref) => ref.id === userRef.id)
}

/**
 * Recupera un documento a partir de su referencia
 *
 * @param {string} ref - referencia al documento a recuperar
 * @returns {Promise<Object>} Una promesa que se resuelve con los datos del documento
 */
export async function GetDocumentFromReference(ref) {
  if (ref) return await GetDocFromReference(ref)
  return null
}

/*
 * Resuelve las referencias a los cifs de un grupo y crea en el grupo un nuevo objeto set con los cifs.
 */
export async function groupWithCifs(group) {
  const refs = group.cifsRef || []

  if (!refs.length) {
    group.cifsSet = new Set()
    return
  }

  const companies = await Promise.all(
    refs.map((ref) => GetDocFromReference(ref))
  )

  group.cifsSet = new Set(
    companies
      .filter((c) => c.notificacionesActivas)
      .map((c) => c.numeroDocIdentidad)
  )
}

/**
 * Comprueba si un grupo dado tiene un cif
 * @param {Object} group documento Group
 * @param {Array} cifs Array de cifs (strings estilo A1234 , B6548...)
 * @param {String} mode modo de funcionamiento: some o every , por defecto every.
 *                      comprobar si alguno de los cifs de la lista ó comprobar si todos deben existir.
 * @returns Boolean
 */
export function groupHasCifs(group, cifs = [], { mode = 'every' } = {}) {
  if (!cifs.length) return true
  // Ojo, tiene que haber pasado previamente por la funcion que está justo encima y tener el objeto cifsSet
  if (!group?.cifsSet) return false

  return mode === 'every'
    ? cifs.every((cif) => group.cifsSet.has(cif))
    : cifs.some((cif) => group.cifsSet.has(cif))
}
